import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState, resolveUser } from '@provider/user-identity';
import { getComponentEntryQueryKey } from './query.get.js';
import { saveComponentEntryRequest } from './request.save.js';
import { fetchEntriesForComponentQueryKey } from '../../api/request.fetch-entries.js';

function useUpdateComponentEntryMutation() {
  const client = useQueryClient();
  const notifier = useNotifier();
  const user = useUserIdentityState(state => state.user);
  const result = useMutation(mutation(client, notifier, user));
  return useMemo(() => ({
    ...result,
    mutate: nextEntry => {
      const prevEntry = client.getQueryData(getComponentEntryQueryKey(nextEntry.entryKey));
      result.mutate({
        nextEntry,
        prevEntry
      });

      // NB: We update our queryData here instead of inside the mutations
      // onMutate callback to ensure that the next value is applied sync.
      // This ensures that e.g. input values based on query data is updated
      // immediately inside change events, which ensures that the caret does
      // not jump.
      const prevEntryKey = JSON.stringify(prevEntry || "null");
      const nextEntryKey = JSON.stringify(nextEntry);
      if (!prevEntry || prevEntryKey !== nextEntryKey) {
        client.setQueryData(getComponentEntryQueryKey(nextEntry.entryKey), () => {
          return {
            ...nextEntry,
            envelope: {
              ...nextEntry.envelope,
              modified: Math.round(Date.now() / 1000)
            },
            state: {
              ...nextEntry.state,
              isMutating: true
            }
          };
        });

        //
        // STEP: Ensure that our fetched list of component entries are up to
        // date with actual local data
        //
        client.setQueryData(fetchEntriesForComponentQueryKey(nextEntry.envelope.userID, nextEntry.envelope.componentID), data => {
          const latestEntry = data?.at(-1);
          if (!data || !latestEntry) {
            return [{
              state: "valid",
              envelope: nextEntry.envelope,
              data: nextEntry.data
            }];
          }
          latestEntry.data = nextEntry.data;
          latestEntry.envelope = nextEntry.envelope;
          return data;
        });
      }
    }
  }), [client, result]);
}
const mutation = (client, notifier, user) => {
  return {
    mutationKey: ["component-framework/update-component-entry"],
    mutationFn: async ({
      nextEntry,
      prevEntry
    }) => {
      let resolvedUser = user;

      // in case the current user has not yet been resolved, then wait before
      // initializing the actual saving process.
      if (user === undefined) {
        resolvedUser = await resolveUser();
      }
      if (!resolvedUser) {
        return {
          changed: false,
          result: undefined
        };
      }
      if (resolvedUser.id !== nextEntry?.envelope.userID) {
        console.warn("useUpdateComponentEntryMutation(): userId does not own entry.");
        return {
          changed: false,
          result: undefined
        };
      }
      const prevEntryKey = JSON.stringify(prevEntry || "null");
      const nextEntryKey = JSON.stringify(nextEntry);
      if (!!prevEntry?.envelope.objectID && prevEntryKey === nextEntryKey) {
        return {
          changed: false,
          result: undefined
        };
      }
      return {
        changed: true,
        result: await saveComponentEntryRequest(resolvedUser, nextEntry)
      };
    },
    onSuccess: ({
      result,
      changed
    }, variables) => {
      if (!variables.nextEntry.entryKey || !changed) {
        return;
      }
      client.setQueryData(getComponentEntryQueryKey(variables.nextEntry.entryKey), current => {
        const next = structuredClone(current ?? result ?? variables.nextEntry);
        next.state.result = "success";
        next.state.isMutating = false;
        next.envelope.modified = Math.max(next.envelope.modified ?? 0, result?.envelope.modified ?? 0);
        next.envelope.objectID = result?.envelope.objectID ?? next.envelope.objectID;

        //
        // STEP: Ensure that our fetched list of component entries are up to
        // date with actual local data
        //
        client.setQueryData(fetchEntriesForComponentQueryKey(variables.nextEntry.envelope.userID, variables.nextEntry.envelope.componentID), data => {
          const latestEntry = data?.at(-1);
          if (!data || !latestEntry) {
            return [{
              state: "valid",
              envelope: next.envelope,
              data: next.data
            }];
          }
          latestEntry.data = next.data;
          latestEntry.envelope = next.envelope;
          return data;
        });
        return next;
      });
    },
    onError: (err, variables) => {
      notifier.notify({
        id: "component-framework.update-component-entry.error",
        type: "error",
        body: "Noget gik galt da vi forsøgte at gemme din besvarelse."
      });
      client.setQueryData(getComponentEntryQueryKey(variables.nextEntry.entryKey), current => {
        const next = current ?? variables.prevEntry ?? variables.nextEntry;
        return {
          ...next,
          state: {
            result: "error",
            isMutating: false
          }
        };
      });
    }
  };
};

export { useUpdateComponentEntryMutation };
//# sourceMappingURL=mutation.update.js.map
